import { RedirectToSignIn, useAuth, useUser } from '@clerk/react'
import type { ReactNode } from 'react'
import { useLocation } from '#router'
import LoadingFallback from './loading-fallback'
import NotFound from './not-found'

interface AdminProtectedRouteProps {
  children: ReactNode
}

/**
 * Checks if the user has admin role in their Clerk publicMetadata.
 * To grant admin access, set publicMetadata.role = "admin" in the Clerk dashboard.
 */
function isAdmin(user: ReturnType<typeof useUser>['user']): boolean {
  if (!user) return false
  const role = user.publicMetadata?.role
  return role === 'admin'
}

export function AdminProtectedRoute({ children }: AdminProtectedRouteProps) {
  const { isLoaded: authLoaded, isSignedIn } = useAuth()
  const { isLoaded: userLoaded, user } = useUser()
  const location = useLocation()
  const currentPathWithSearch = `${location.pathname}${location.search}`

  // Show loading state while checking authentication
  if (!authLoaded || !userLoaded) {
    return <LoadingFallback />
  }

  // Redirect to sign in if not authenticated
  if (!isSignedIn) {
    return <RedirectToSignIn redirectUrl={currentPathWithSearch} />
  }

  // Show 404 if user is not an admin (don't reveal that admin routes exist)
  if (!isAdmin(user)) {
    return <NotFound />
  }

  return <>{children}</>
}

/**
 * Hook to check if the current user is an admin.
 * Useful for conditionally rendering admin-only UI elements.
 */
export function useIsAdmin(): { isAdmin: boolean; isLoaded: boolean } {
  const { isLoaded: userLoaded, user } = useUser()

  return {
    isAdmin: isAdmin(user),
    isLoaded: userLoaded,
  }
}
